import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Layers, FolderTree, FileStack } from 'lucide-react';
import AdminModulesTab from '@/pages/admin/tabs/AdminModulesTab';
import AdminFoldersTab from '@/pages/admin/tabs/AdminFoldersTab';
import AdminContentTab from '@/pages/admin/tabs/AdminContentTab';
import { useContent } from '@/pages/admin/hooks/useContent';

const SPACE = 'platinum_plus';

const TABS = [
  { key: 'modules', label: 'Trainings', icon: Layers },
  { key: 'folders', label: 'Sections', icon: FolderTree },
  { key: 'content', label: 'Content', icon: FileStack },
];

/** The main platform's content manager, pointed at Platinum+. Same forms, same uploaders. */
export const PlatinumContentManager = () => {
  const [tab, setTab] = useState('modules');
  const [modules, setModules] = useState([]);
  const [folders, setFolders] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState({});
  const contentFormRef = useRef(null);
  const content = useContent(SPACE);

  const loadShape = async () => {
    try {
      const [m, f] = await Promise.all([
        axios.get(`${API}/modules?space=${SPACE}`),
        axios.get(`${API}/content-folders?space=${SPACE}`),
      ]);
      setModules(m.data);
      setFolders(f.data);
    } catch (e) {
      toast.error('Could not load the Platinum+ trainings');
    }
  };

  useEffect(() => { loadShape(); content.fetchContents(); }, [tab]);

  return (
    <div data-testid="platinum-content-manager">
      <div style={{ display: 'flex', gap: '.5rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {TABS.map((t) => {
          const Icon = t.icon;
          const on = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`platinum-content-tab-${t.key}`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '.4rem', padding: '.5rem 1.1rem',
                borderRadius: 999, fontWeight: 600, fontSize: '.95rem',
                background: on ? 'linear-gradient(135deg,#7F0D6C,#5C0A4E)' : 'rgba(255,253,248,0.8)',
                color: on ? '#FFFDD0' : '#5C0A4E',
                border: `1px solid ${on ? 'transparent' : 'rgba(127,13,108,0.35)'}`,
              }}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="pp-skin">
        {tab === 'modules' && <AdminModulesTab space={SPACE} />}
        {tab === 'folders' && <AdminFoldersTab modules={modules} space={SPACE} />}
        {tab === 'content' && (
          <AdminContentTab
            contents={content.contents}
            contentForm={content.contentForm}
            setContentForm={content.setContentForm}
            editingContent={content.editingContent}
            contentFormRef={contentFormRef}
            modules={modules}
            folders={folders}
            photoOptions={content.photoOptions}
            selectedPhoto={content.selectedPhoto}
            setSelectedPhoto={content.setSelectedPhoto}
            loadingPhotos={content.loadingPhotos}
            showUnsplashModal={content.showUnsplashModal}
            setShowUnsplashModal={content.setShowUnsplashModal}
            unsplashSearchQuery={content.unsplashSearchQuery}
            setUnsplashSearchQuery={content.setUnsplashSearchQuery}
            unsplashResults={content.unsplashResults}
            expandedModules={content.expandedModules}
            setExpandedModules={() => {}}
            expandedFolders={expandedFolders}
            setExpandedFolders={setExpandedFolders}
            viewPhotoModal={content.viewPhotoModal}
            setViewPhotoModal={content.setViewPhotoModal}
            selectedPhotoUrl={content.selectedPhotoUrl}
            setSelectedPhotoUrl={content.setSelectedPhotoUrl}
            selectedPhotoAttribution={content.selectedPhotoAttribution}
            setSelectedPhotoAttribution={content.setSelectedPhotoAttribution}
            createContent={() => content.createContent(content.fetchContents)}
            fetchPhotoOptions={content.fetchPhotoOptions}
            searchUnsplash={content.searchUnsplash}
            openUnsplashBrowser={content.openUnsplashBrowser}
            selectPhotoFromModal={content.selectPhotoFromModal}
            handleDeleteContent={(id) => content.handleDeleteContent(id, content.fetchContents)}
            handleEditContent={(c) => content.handleEditContent(c, contentFormRef)}
            resetContentForm={content.resetContentForm}
            toggleModuleExpansion={content.toggleModuleExpansion}
          />
        )}
      </div>
    </div>
  );
};

export default PlatinumContentManager;
